import React, { useEffect, useState } from 'react';
import Modal from './Modal';

// CS:GO-style case opening: a horizontal strip of posters scrolls past a fixed center pointer,
// easing out to a stop on a randomly chosen winner. Re-usable across every to-do list - only
// needs {id, title, poster_url}.
const ITEM_WIDTH = 110; // px, matches .spin-card width + margins
const VIEWPORT_WIDTH = 400; // px
const REEL_LENGTH = 36;
// Where the winner can land varies each spin (within this range) so the stop distance/position
// isn't identical every time - leaves enough cards on both sides so the strip never looks "empty".
const MIN_WINNER_INDEX = 20;
const MAX_WINNER_INDEX = 30;
const SPIN_DURATION = 4500; // ms
// How far off dead-center the pointer may land within the winning card (card is 100px wide, i.e.
// +/-50px edge to edge - 45 lets it land almost at either edge while staying clearly on the card,
// not bleeding onto a neighbor) - makes every stop look a little different instead of landing
// picture-perfect in the middle every single time.
const MAX_JITTER = 45; // px

const buildReel = (items) => {
  const winnerIndex = MIN_WINNER_INDEX + Math.floor(Math.random() * (MAX_WINNER_INDEX - MIN_WINNER_INDEX + 1));
  const reel = Array.from({ length: REEL_LENGTH }, () => items[Math.floor(Math.random() * items.length)]);
  const winner = items[Math.floor(Math.random() * items.length)];
  reel[winnerIndex] = winner;
  return { reel, winner, winnerIndex };
};

const offsetFor = (winnerIndex, jitter = 0) =>
  -(winnerIndex * ITEM_WIDTH + ITEM_WIDTH / 2 - VIEWPORT_WIDTH / 2) + jitter;

const CONFETTI_COUNT = 40;

const makeConfetti = () =>
  Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
    id: i,
    left: 50 + (Math.random() - 0.5) * 60,
    drift: (Math.random() - 0.5) * 140,
    size: 6 + Math.random() * 6,
    rotate: Math.random() * 360,
    duration: 1100 + Math.random() * 900,
    delay: Math.random() * 200,
  }));

function SpinModal({ items, onClose }) {
  const [reel, setReel] = useState([]);
  const [winner, setWinner] = useState(null);
  const [winnerIndex, setWinnerIndex] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [offset, setOffset] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [confetti, setConfetti] = useState([]);

  const spin = () => {
    if (!items.length) return;
    const { reel: newReel, winner: newWinner, winnerIndex: newWinnerIndex } = buildReel(items);
    setReel(newReel);
    setWinner(null);
    setWinnerIndex(newWinnerIndex);
    setSpinning(true);
    setTransitioning(false);
    setOffset(0);
    setConfetti([]);

    const jitter = (Math.random() - 0.5) * 2 * MAX_JITTER;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTransitioning(true);
        setOffset(offsetFor(newWinnerIndex, jitter));
      });
    });

    setTimeout(() => {
      setSpinning(false);
      setWinner(newWinner);
    }, SPIN_DURATION + 50);
  };

  useEffect(() => {
    spin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!winner) return;
    setConfetti(makeConfetti());
    const t = setTimeout(() => setConfetti([]), 2200);
    return () => clearTimeout(t);
  }, [winner]);

  return (
    <Modal onClose={onClose}>
      {confetti.map((c) => (
        <div
          key={c.id}
          className="confetti-piece"
          style={{
            left: `${c.left}%`,
            width: c.size,
            height: c.size * 1.6,
            '--drift': `${c.drift}px`,
            '--rotate-start': `${c.rotate}deg`,
            animationDuration: `${c.duration}ms`,
            animationDelay: `${c.delay}ms`,
          }}
        />
      ))}

      <h2 className="modal-title">Spin!</h2>

      {items.length === 0 ? (
        <p className="no-results">Niks om uit te kiezen.</p>
      ) : (
        <>
          <div className="spin-viewport" style={{ width: VIEWPORT_WIDTH }}>
            <div className="spin-pointer" />
            <div
              className="spin-track"
              style={{
                transform: `translateX(${offset}px)`,
                transition: transitioning ? `transform ${SPIN_DURATION}ms cubic-bezier(0.12, 0.76, 0.18, 1)` : 'none',
              }}
            >
              {reel.map((it, i) => (
                <div className={`spin-card${!spinning && winner && i === winnerIndex ? ' spin-card--winner' : ''}`} key={i}>
                  {it?.poster_url && <img src={it.poster_url} alt={it.title} />}
                </div>
              ))}
            </div>
          </div>

          <p className="spin-result" style={{ visibility: winner && !spinning ? 'visible' : 'hidden' }}>
            {winner?.title || ' '}
          </p>
        </>
      )}

      <div className="edit-modal-actions">
        <button type="button" className="form-submit btn-cancel" onClick={onClose}>Close</button>
        <button type="button" className="form-submit" onClick={spin} disabled={spinning || items.length === 0}>
          {spinning ? 'Spinning...' : 'Spin again'}
        </button>
      </div>
    </Modal>
  );
}

export default SpinModal;
